import { Dialog, DialogBody } from '@material-tailwind/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClockLoader } from 'react-spinners';
import { useUserContext } from '../../context';
import { useApi } from '../../hooks/useApi';

export default function ViewOTFTaxInvoice() {
    const { invoiceNo } = useParams();
    const { fetchAccessToken, defaultHotel, assignments } = useUserContext();
    const [isLoading, setIsLoading] = useState(false);
    const { getOTFTaxInvoiceByNo, downloadOTFTaxInvoice } = useApi();
    const [hotelDetails, setHotelDetails] = useState({});
    const [taxInvoice, setTaxInvoice] = useState(null);


    useEffect(() => {
        let getInvoiceInternal = async (hotelId, invoiceNo) => {
            setIsLoading(true);
            try {
                const token = await fetchAccessToken();
                const taxInvoice = await getOTFTaxInvoiceByNo(hotelId, invoiceNo, token);
                if (taxInvoice) {
                    setTaxInvoice(taxInvoice);
                } else {
                    alert("Invoice doesn't exist in the hotel.");
                }
            } catch (ex) {
                alert("Something went wrong.");
            } finally {
                setIsLoading(false);
            }
        }
        if (invoiceNo && defaultHotel) {
            getInvoiceInternal(defaultHotel, invoiceNo);
        }
    }, [invoiceNo, defaultHotel]);


    useEffect(() => {
        if (defaultHotel) {
            let hotel = assignments?.hotels.find(hotel => hotel.id.toLowerCase() === defaultHotel.toLowerCase());
            setHotelDetails(hotel);
        }
    }, [defaultHotel]);

    function formatDateFromEpoch(epoch) {
        if (!epoch)
            return "";
        const date = new Date(epoch);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    }

    const printInvoice = async () => {

        if (invoiceNo && defaultHotel && taxInvoice) {
            try {
                setIsLoading(true);
                const token = await fetchAccessToken();
                await downloadOTFTaxInvoice(defaultHotel, invoiceNo, token);
            } catch (ex) {
                alert("Something went wrong.");
            } finally {
                setIsLoading(false);
            }
        }
    }

    function getGSTPercentage(taxInvoice) {
        return taxInvoice.gstPercentage || 5;
    }

    function getBaseAmount(taxInvoice) {
        const rate = getGSTPercentage(taxInvoice) / 100;
        let basePrice = Math.round(taxInvoice.gstAmount / (1 + rate));
        return basePrice;
    }

    function getGSTValue(taxInvoice) {
        return taxInvoice.gstAmount - getBaseAmount(taxInvoice);
    }

    function getCGST(taxInvoice) {
        return Math.floor(getGSTValue(taxInvoice) / 2);
    }

    function getSGST(taxInvoice) {
        const totalGST = getGSTValue(taxInvoice);
        return totalGST - getCGST(taxInvoice);
    }

    function getHalfGST(taxInvoice) {
        const gstPercent = getGSTPercentage(taxInvoice);
        return gstPercent / 2;
    }

    return (
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-lg bg-blueGray-100 border border-blue-gray-100 border-1 ">
            {taxInvoice && <div className="rounded-lg bg-white p-8 text-xs">
                <div style={{ height: '180px' }} className="overflow-x-auto">
                    <img src={`https://www.innpro.in/headers/${defaultHotel.toUpperCase()}.jpg`} className="h-full min-w-[600px] w-auto" />
                </div>
                <div className="text-center mb-4">
                    <p className="text-gray-900 text-xl font-semibold"><u>TAX INVOICE</u></p>
                </div>
                <hr />
                <div className="mb-6">
                    <table className="w-full text-gray-900 border-collapse">
                        <tbody>
                            <tr>
                                <td className="border py-2 px-4 w-1/4"><span className="font-semibold">Invoice No</span></td>
                                <td className="border py-2 px-4 w-1/4">{taxInvoice.invoiceNo}</td>
                                <td className="border py-2 px-4 w-1/4"><span className="font-semibold">Date</span></td>
                                <td className="border py-2 px-4 w-1/4">{formatDateFromEpoch(taxInvoice.invoiceDateEpoch)}</td>
                            </tr>
                            <tr>
                                <td className="border py-2 px-4"><span className="font-semibold">Customer Details</span></td>
                                <td className="border py-2 px-4 uppercase">{taxInvoice.guestName} <br /> {taxInvoice.guestMobile}</td>
                                <td className="border py-2 px-4"><span className="font-semibold">Company Details</span></td>
                                <td className="border py-2 px-4 uppercase">{taxInvoice.companyDetails}</td>
                            </tr>
                            <tr>
                                <td className="border py-2 px-4"><span className="font-semibold">Customer GST No</span></td>
                                <td className="border py-2 px-4 uppercase">{taxInvoice.companyGST}</td>
                                <td className="border py-2 px-4"><span className="font-semibold">HSN Code</span></td>
                                <td className="border py-2 px-4">{taxInvoice.invoiceType === 'FOOD' ? (taxInvoice.roomDetails?.[0]?.hsnCode || 'N/A') : '9963'}</td>
                            </tr>
                            <tr>
                                <td className="border py-2 px-4" colSpan={4}><span className="font-semibold">{taxInvoice.invoiceType === 'FOOD' ? 'Service Details' : 'Room Details'}</span></td>
                            </tr>
                            <tr>
                                <td className="border" colSpan={4}>
                                    {taxInvoice.invoiceType === 'FOOD' ? (
                                        <table className="w-full divide-y divide-gray-200 bg-white overflow-hidden">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Description</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">HSN/SAC</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">PAX</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Rate</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {taxInvoice?.roomDetails?.map((detail, index) => (
                                                    <tr key={index}>
                                                        <td className="border px-6 py-4 text-gray-900">{detail.description}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{detail.hsnCode}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{detail.count}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{detail.rate}.00</td>
                                                        <td className="border px-6 py-4 text-gray-900">{(detail.count * detail.rate).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <table className="w-full divide-y divide-gray-200 bg-white overflow-hidden">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Room No</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Category</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">In Date & Time</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Out Date & Time</th>
                                                    <th className="border px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase">Days</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {taxInvoice?.roomDetails?.map((roomDetail, index) => (
                                                    <tr key={index}>
                                                        <td className="border px-6 py-4 text-gray-900">{roomDetail.roomNo}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{roomDetail.category}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{taxInvoice.checkInDateTime}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{taxInvoice.checkOutDateTime}</td>
                                                        <td className="border px-6 py-4 text-gray-900">{roomDetail.totalDays}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="border py-2 px-4" colSpan={4}><span className="font-semibold">Price Details</span></td>
                            </tr>
                            <tr>
                                <td className="border py-2 px-4" colSpan={2}>
                                    <span className="font-semibold">Bank Details</span>
                                    <pre>
                                        {hotelDetails.bankDetails
                                            ? hotelDetails.bankDetails.split(',').map((line, index) => (
                                                <div key={index}>{line.trim()}</div>
                                            ))
                                            : ''}
                                    </pre>
                                </td>
                                <td className="border py-2 px-4" colSpan={2}>
                                    <table className="w-full divide-y divide-gray-200 bg-white overflow-hidden rounded-lg">
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td className="text-xs border px-6 py-4 text-gray-900">Base Price</td>
                                                <td className="border px-6 py-4 text-gray-900">{taxInvoice.gst ? getBaseAmount(taxInvoice) : taxInvoice.gstAmount}.00</td>
                                            </tr>
                                            {taxInvoice.gst &&
                                                <tr>
                                                    <td className="text-xs border px-6 py-4 text-gray-900">CGST {getHalfGST(taxInvoice)}%</td>
                                                    <td className="border px-6 py-4 text-gray-900">{getCGST(taxInvoice)}.00</td>
                                                </tr>
                                            }
                                            {taxInvoice.gst &&
                                                <tr>
                                                    <td className="text-xs border px-6 py-4 text-gray-900">SGST {getHalfGST(taxInvoice)}%</td>
                                                    <td className="border px-6 py-4 text-gray-900">{getSGST(taxInvoice)}.00</td>
                                                </tr>
                                            }
                                            <tr>
                                                <td className="text-xs border px-6 py-4 text-gray-900">Total Amount</td>
                                                <td className="border px-6 py-4 text-gray-900">{taxInvoice.gstAmount}.00</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            }
            <div className="flex justify-center m-2">
                <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 mr-2 rounded"
                    onClick={printInvoice}>Print</button>
            </div>
            <Dialog open={isLoading} className="bg-transparent shadow-none z-60" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>
        </div >
    )
}
